import { ApiError, api } from './api'
import type {
  DocumentCategory,
  DocumentVisibility,
  EstateDocument,
  PaginationMeta,
} from '@riverpark/types'

const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? '/api'

/** Unlike API_BASE, this can't fall back to the SPA's own relative "/api" proxy path — the URL
 * it builds gets embedded as static <img src> HTML and re-rendered on other origins entirely
 * (the public landing site, announcement emails), so it must always be absolute. */
const ABSOLUTE_API_BASE =
  import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? 'http://localhost:8787/api'

/** Always-unauthenticated download URL — used for inline images (always `public` visibility),
 * embeddable directly as an <img src> with no auth dance. */
export function publicDocumentUrl(documentId: string): string {
  return `${ABSOLUTE_API_BASE}/documents/public/${documentId}/download`
}

export interface ListDocumentsFilters {
  page?: number
  limit?: number
  category?: DocumentCategory
}

export async function listDocuments(
  filters: ListDocumentsFilters = {},
): Promise<{ data: EstateDocument[]; meta?: PaginationMeta }> {
  const params = new URLSearchParams()
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.category) params.set('category', filters.category)
  const query = params.toString()
  return api.get<{ data: EstateDocument[]; meta?: PaginationMeta }>(
    `/documents${query ? `?${query}` : ''}`,
  )
}

export interface UploadDocumentInput {
  title: string
  category: DocumentCategory
  visibility: DocumentVisibility
  memberId?: string
  plotId?: string
  file: File
}

export async function uploadDocument(
  input: UploadDocumentInput,
): Promise<{ data: EstateDocument }> {
  const form = new FormData()
  form.set('title', input.title)
  form.set('category', input.category)
  form.set('visibility', input.visibility)
  if (input.memberId) form.set('memberId', input.memberId)
  if (input.plotId) form.set('plotId', input.plotId)
  form.set('file', input.file)
  return api.postForm<{ data: EstateDocument }>('/documents', form)
}

export interface UpdateDocumentInput {
  title: string
  category: DocumentCategory
  visibility: DocumentVisibility
  memberId?: string
  plotId?: string
  /** Omit to keep the currently-stored file. */
  file?: File
}

export async function updateDocument(
  id: string,
  input: UpdateDocumentInput,
): Promise<{ data: EstateDocument }> {
  const form = new FormData()
  form.set('title', input.title)
  form.set('category', input.category)
  form.set('visibility', input.visibility)
  if (input.memberId) form.set('memberId', input.memberId)
  if (input.plotId) form.set('plotId', input.plotId)
  if (input.file) form.set('file', input.file)
  return api.patchForm<{ data: EstateDocument }>(`/documents/${id}`, form)
}

export async function deleteDocument(id: string): Promise<{ data: { success: boolean } }> {
  return api.delete<{ data: { success: boolean } }>(`/documents/${id}`)
}

function extensionForMimeType(mimeType: string): string {
  const map: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'image/jpeg': 'jpg',
    'image/png': 'png',
  }
  return map[mimeType.toLowerCase()] ?? 'bin'
}

async function fetchDocumentBlob(id: string, view: boolean): Promise<Blob> {
  const res = await fetch(`${API_BASE}/documents/${id}/download${view ? '?view=1' : ''}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(res.status, 'Failed to fetch document')
  return res.blob()
}

/** Fetches the real binary from the API and triggers a client-side save — the API lives on a
 * separate subdomain, so a plain `<a href>` would bypass the auth cookie. */
export async function downloadDocument(doc: EstateDocument): Promise<void> {
  const blob = await fetchDocumentBlob(doc.id, false)
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${doc.title.replace(/[^a-zA-Z0-9._-]/g, '_')}.${extensionForMimeType(doc.mimeType)}`
  anchor.click()
  URL.revokeObjectURL(url)
}

/** Same as downloadDocument, for callers that only have {id, title, mimeType} rather than a full
 * EstateDocument — e.g. a document-request item's linked submission. */
export async function downloadDocumentById(
  id: string,
  title: string,
  mimeType: string,
): Promise<void> {
  const blob = await fetchDocumentBlob(id, false)
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${title.replace(/[^a-zA-Z0-9._-]/g, '_')}.${extensionForMimeType(mimeType)}`
  anchor.click()
  URL.revokeObjectURL(url)
}

const VIEWABLE_MIME_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'])

/** Only PDFs and images render inline in a browser tab — everything else would just download anyway. */
export function isViewableMimeType(mimeType: string): boolean {
  return VIEWABLE_MIME_TYPES.has(mimeType.toLowerCase())
}

/** Same cross-subdomain auth-cookie constraint as downloadDocument, but opens the file inline in a
 * new tab instead of saving it. */
export async function viewDocument(doc: EstateDocument): Promise<void> {
  const blob = await fetchDocumentBlob(doc.id, true)
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}

/** Same as viewDocument, for callers that only have {id, mimeType} — see downloadDocumentById. */
export async function viewDocumentById(id: string): Promise<void> {
  const blob = await fetchDocumentBlob(id, true)
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}
